import { useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

interface AuthCheckSuccessContext<TSession> {
  session: TSession;
  router: AppRouterInstance;
}

interface AuthCheckFailureContext {
  router: AppRouterInstance;
  requireAuth: boolean;
  response?: Response;
  error?: unknown;
}

interface UseAuthCheckCoreOptions<TSession> {
  requireAuth?: boolean;
  fetchSession: () => Promise<Response>;
  parseSession?: (response: Response) => Promise<TSession>;
  onAuthenticated: (context: AuthCheckSuccessContext<TSession>) => void;
  onUnauthenticated: (context: AuthCheckFailureContext) => void;
  onError?: (context: AuthCheckFailureContext) => void;
}

export function useAuthCheckCore<TSession>({
  requireAuth = false,
  fetchSession,
  parseSession,
  onAuthenticated,
  onUnauthenticated,
  onError,
}: UseAuthCheckCoreOptions<TSession>) {
  const router = useRouter();
  const parse = useCallback(
    (response: Response) => (parseSession ? parseSession(response) : (response.json() as Promise<TSession>)),
    [parseSession],
  );

  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const response = await fetchSession();
        if (cancelled) {
          return;
        }

        if (response.ok) {
          const session = await parse(response);

          if (!cancelled) {
            onAuthenticated({ session, router });
          }

          return;
        }

        if (!cancelled) {
          onUnauthenticated({ router, requireAuth, response });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        const handler = onError ?? onUnauthenticated;
        handler({ router, requireAuth, error });
      }
    }

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, [fetchSession, parse, onAuthenticated, onUnauthenticated, onError, requireAuth, router]);
}
